import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useOrgData } from '../hooks/useOrgData';
import { updateStage } from '../lib/data';
import { cpvUnitario, desviosSabor, premissasCompletas, premissasFaltando, NOME_ETAPA_VIABILIDADE, ETAPA_VIABILIDADE_PADRAO, type Premissas } from '../shared/premissas';
import { compararPco, gramaturasDiferem } from '../shared/ancoragem';
import {
  BLOCK_AREAS,
  BLOCK_TITLES,
  capaBlocks,
  type BlockKey,
  type Competidor,
  type ProductCard,
  type FlavorRow,
  type VendaSabor,
  type BlockVersion,
  addBlockHistory,
  blockComplete,
  canEditBlock,
  emptyCard,
  loadBlockHistory,
  loadCard,
  margemPrevista,
  viabilidadeComDerivados,
  saveBlock,
  saveFlavor,
  upsertVendaSabor,
  vendaMargem,
  vendaSaborFor,
  vendaVolume12m,
  vendasPrecoMedioKg,
  vendasTotais,
  vendasVolumeMensalAgregado,
} from '../lib/capa';
import {
  CATEGORY_LABELS,
  PriorityBadge,
  StatusBadge,
  fmtDate,
  metaInfo,
} from '../components/ui';
import { FloatingBack } from '../components/FloatingBack';
import { computeProductSummary } from '../shared/criticalPath';
import { buildTemplateForType } from '../shared/terceirizacao';
import { exportCapaCSV } from '../lib/exportCapa';

const MESES = ['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'];
const brl = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const num = (v: string): number | undefined => (v === '' ? undefined : Number(v));

export function Capa() {
  const { id } = useParams();
  const { currentOrgId, grants, areas, fullName, userId } = useAuth();
  const { loading: loadingProducts, products, template, refresh } = useOrgData(currentOrgId);
  const product = products.find((p) => p.id === id);
  const summary = useMemo(
    () =>
      product
        ? computeProductSummary(
            product,
            buildTemplateForType(template, product.projectType ?? 'interno'),
            new Date(),
          )
        : null,
    [product, template],
  );

  const [card, setCard] = useState<ProductCard>(emptyCard());
  const [flavors, setFlavors] = useState<FlavorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<BlockKey | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState<Set<BlockKey>>(new Set());
  const [histFor, setHistFor] = useState<BlockKey | null>(null);
  const [histRows, setHistRows] = useState<BlockVersion[]>([]);
  const [histLoading, setHistLoading] = useState(false);

  const areaLabel = useMemo(() => {
    const m = new Map(areas.map((a) => [a.key, a.name]));
    return (k: string) => m.get(k) ?? k;
  }, [areas]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    loadCard(id)
      .then(({ card, flavors }) => {
        setCard(card);
        setFlavors(flavors);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loadingProducts || loading) return <div className="center-note">Carregando…</div>;
  if (!product || !grants || !id)
    return (
      <div className="center-note">
        Projeto não encontrado. <Link to="/">Voltar ao painel</Link>
      </div>
    );

  const category = product.category ?? 'inovacao';
  const melhoria = category === 'melhoria';
  const blocks = capaBlocks(category);
  const completos = blocks.filter((b) => blockComplete(b, card, category)).length;
  const totalBlocos = blocks.length;

  const upd = <K extends BlockKey>(key: K, val: ProductCard[K]) =>
    setCard((c) => ({ ...c, [key]: val }));

  function primaryAreaFor(block: BlockKey): string {
    const mine = BLOCK_AREAS[block].find((a) => grants!.areaKeys.includes(a));
    return mine ?? BLOCK_AREAS[block][0] ?? '—';
  }

  const isAdminUser = grants.role === 'admin';
  const isSaved = (b: BlockKey) => Boolean(card.governanca[b]?.at);
  // Um bloco "congela" quando está salvo E com todos os campos (obrigatórios)
  // preenchidos. Aí trava os campos para TODOS (inclusive admin); só admin
  // reativa. Salvo mas ainda incompleto segue editável para o responsável
  // terminar de preencher.
  const isCongelado = (b: BlockKey) => isSaved(b) && blockComplete(b, card, category);
  function blockEditable(b: BlockKey): boolean {
    const responsible = canEditBlock(grants!.role, grants!.areaKeys, b);
    if (!responsible) return false;
    if (unlocked.has(b)) return true; // admin reativou nesta sessão
    return !isCongelado(b);
  }

  async function openHistory(b: BlockKey) {
    if (!id) return;
    setHistFor(b);
    setHistLoading(true);
    setHistRows(await loadBlockHistory(id, b));
    setHistLoading(false);
  }

  async function save(key: BlockKey) {
    if (!id) return;
    setSavingKey(key);
    setMsg(null);
    const gov = {
      ...card.governanca,
      [key]: {
        by: fullName ?? undefined,
        area: areaLabel(primaryAreaFor(key)),
        at: new Date().toISOString().slice(0, 10),
      },
    };
    // CPV/un. é sempre recalculado (CPV/kg × gramatura) antes de salvar.
    const valor = key === 'viabilidade' ? viabilidadeComDerivados(card.viabilidade, card.identificacao.gramatura) : card[key];
    const { error } = await saveBlock(id, key, valor, gov);
    if (error) setMsg(`Erro ao salvar ${BLOCK_TITLES[key]}: ${error}`);
    else {
      // Guarda a versão no histórico e recongela o bloco.
      if (currentOrgId) await addBlockHistory(currentOrgId, id, key, valor, userId, fullName);
      setCard((c) => ({ ...c, [key]: valor, governanca: gov }));
      setUnlocked((s) => {
        const n = new Set(s);
        n.delete(key);
        return n;
      });
      let extra = '';
      if (key === 'viabilidade' && premissasCompletas(valor as Premissas)) {
        // Premissas completas → conclui a etapa "Análise de Viabilidade Financeira" (PD1) automaticamente.
        const n = template.find((t) => t.name === NOME_ETAPA_VIABILIDADE)?.n ?? ETAPA_VIABILIDADE_PADRAO;
        const inst = product?.stages.find((s) => s.n === n);
        if (inst?.status !== 'concluido') {
          const hoje = new Date().toISOString().slice(0, 10);
          const r = await updateStage(id, n, {
            status: 'concluido',
            started_at: inst?.startedAt ?? hoje,
            finished_at: inst?.finishedAt ?? hoje,
          });
          extra = r.error
            ? ` Não consegui concluir a etapa ${n} automaticamente (${r.error}) — peça a quem é da área para concluir.`
            : ` A etapa ${n} (${NOME_ETAPA_VIABILIDADE}) foi concluída automaticamente no PD1.`;
          refresh();
        }
      }
      setMsg(`${BLOCK_TITLES[key]} salvo e registrado no histórico.${extra}`);
    }
    setSavingKey(null);
  }

  function SaveBar({ blockKey }: { blockKey: BlockKey }) {
    const responsible = canEditBlock(grants!.role, grants!.areaKeys, blockKey);
    const saved = isSaved(blockKey);
    const congelado = isCongelado(blockKey);
    const editable = blockEditable(blockKey);
    return (
      <div className="capa-savebar">
        {!responsible && (
          <span className="muted">
            Somente {BLOCK_AREAS[blockKey].map(areaLabel).join(' / ')} (ou PMO) editam este bloco.
          </span>
        )}
        {saved && (
          <button type="button" className="linklike" onClick={() => openHistory(blockKey)}>
            Ver histórico
          </button>
        )}
        {congelado && !editable && isAdminUser && (
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setUnlocked((s) => new Set(s).add(blockKey))}
          >
            🔓 Reativar bloco
          </button>
        )}
        {congelado && !editable && (
          <span className="capa-frozen">
            🔒 congelado{!isAdminUser ? ' — peça a um admin para reativar' : ''}
          </span>
        )}
        {editable && (
          <button className="btn-primary btn-sm" disabled={savingKey === blockKey} onClick={() => save(blockKey)}>
            {savingKey === blockKey ? 'Salvando…' : 'Salvar bloco'}
          </button>
        )}
      </div>
    );
  }

  function Seal({ blockKey }: { blockKey: BlockKey }) {
    const done = blockComplete(blockKey, card, category);
    return <span className={`seal ${done ? 'ok' : 'pend'}`}>{done ? 'Completo' : 'Pendente'}</span>;
  }

  const canId = blockEditable('identificacao');

  return (
    <>
      <div className="topbar">
        <div>
          <div className="muted" style={{ fontSize: 12 }}>
            <Link to="/">Painel</Link> · {product.brand} ·{' '}
            <Link to={`/produto/${id}`}>Etapas do projeto</Link>
          </div>
          <h1>Capa do Projeto — {product.name}</h1>
        </div>
        <div className="stat-strip">
          <div className="stat-chip">
            <span className="n tabular">
              {completos}/{totalBlocos}
            </span>
            <span className="l">Blocos completos</span>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="capa-statusbar">
          {summary && (
            <>
              <StatusBadge status={summary.status} />
              <span className="csb-item">Fase atual: <b>{summary.faseAtual}</b></span>
              <span className="csb-item">Previsão: <b>{fmtDate(summary.previsaoConclusao)}</b></span>
              {summary.targetLaunch && (
                <span className="csb-item">
                  Meta: <b>{fmtDate(summary.targetLaunch)}</b>{' '}
                  <em>({metaInfo(summary.desvioMeta).text})</em>
                </span>
              )}
            </>
          )}
          <span className="capa-export no-print">
            <button className="btn-ghost btn-sm" onClick={() => window.print()}>
              🖨️ Exportar PDF
            </button>
            <button
              className="btn-ghost btn-sm"
              onClick={() => exportCapaCSV(product, summary, card, flavors)}
            >
              ⬇️ Exportar Excel
            </button>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
          <PriorityBadge priority={product.priority ?? 'media'} />
          <span className="cat-tag">{CATEGORY_LABELS[product.category ?? 'inovacao']}</span>
        </div>

        {/* Stepper */}
        {!melhoria && (
          <div className="card card-pad capa-stepper">
            {blocks.map((b, i) => (
              <a
                key={b}
                href={`#bloco-${b}`}
                className={`step ${blockComplete(b, card, category) ? 'done' : ''}`}
              >
                <span className="step-dot">{blockComplete(b, card, category) ? '✓' : i + 1}</span>
                <span className="step-label">{BLOCK_TITLES[b]}</span>
              </a>
            ))}
          </div>
        )}
        {melhoria && (
          <p className="muted" style={{ margin: '4px 2px 8px' }}>
            Projeto de <b>Melhoria de produto</b> — capa simplificada: apenas o Resumo Estratégico.
          </p>
        )}

        {msg && <div className="card card-pad ok" style={{ padding: '10px 14px' }}>{msg}</div>}

        {/* 1. Resumo Estratégico */}
        <Block
          blockKey="estrategia"
          title={melhoria ? 'Resumo Estratégico' : '1. Resumo Estratégico'}
          SaveBar={SaveBar}
          Seal={Seal}
        >
          {(() => {
            const can = blockEditable('estrategia');
            const e = card.estrategia;
            const set = (patch: Partial<typeof e>) => upd('estrategia', { ...e, ...patch });
            if (melhoria) {
              return (
                <div className="capa-grid one">
                  <Area label="Objetivo estratégico" value={e.objetivo} disabled={!can} onChange={(v) => set({ objetivo: v })} />
                  <Area label="Outros comentários" value={e.outros_comentarios} disabled={!can} onChange={(v) => set({ outros_comentarios: v })} />
                </div>
              );
            }
            return (
              <div className="capa-grid one">
                <Area label="Racional do lançamento" value={e.racional} disabled={!can} onChange={(v) => set({ racional: v })} />
                <Area label="Público-alvo" value={e.publico_alvo} disabled={!can} onChange={(v) => set({ publico_alvo: v })} />
                <Area label="Proposta de valor / diferencial" value={e.proposta_valor} disabled={!can} onChange={(v) => set({ proposta_valor: v })} />
                <Area label="Objetivo estratégico" value={e.objetivo} disabled={!can} onChange={(v) => set({ objetivo: v })} />
                <Area label="Principais riscos" value={e.riscos} disabled={!can} onChange={(v) => set({ riscos: v })} />
              </div>
            );
          })()}
        </Block>

        {!melhoria && (
        <>
        {/* 2. Identificação */}
        <div className="card card-pad capa-block" id="bloco-identificacao">
          <div className="section-title">
            <h2>2. Identificação</h2>
            <Seal blockKey="identificacao" />
          </div>
          <div className="capa-grid">
            <Text label="Categoria / Linha" value={card.identificacao.categoria_linha} disabled={!canId}
              onChange={(v) => upd('identificacao', { ...card.identificacao, categoria_linha: v })} />
            <Text label="Tipo de embalagem" value={card.identificacao.tipo_embalagem} disabled={!canId}
              onChange={(v) => upd('identificacao', { ...card.identificacao, tipo_embalagem: v })} />
            <Text label="Gramatura / Volume" value={card.identificacao.gramatura} disabled={!canId}
              onChange={(v) => upd('identificacao', { ...card.identificacao, gramatura: v })} />
            <Text label="Un. por caixa" value={card.identificacao.un_caixa} disabled={!canId}
              onChange={(v) => upd('identificacao', { ...card.identificacao, un_caixa: v })} />
            <Text label="Validade / Shelf life" value={card.identificacao.validade} disabled={!canId}
              onChange={(v) => upd('identificacao', { ...card.identificacao, validade: v })} />
          </div>
          <h3 className="capa-sub">Por sabor (código EAN-13 e DUN-14)</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Sabor</th><th>Código EAN-13 (unidade)</th><th>DUN-14 (caixa)</th></tr>
              </thead>
              <tbody>
                {flavors.map((f) => (
                  <tr key={f.id}>
                    <td style={{ fontWeight: 600 }}>{f.name}</td>
                    {(['ean', 'dun'] as const).map((col) => (
                      <td key={col}>
                        <input
                          defaultValue={f[col] ?? ''}
                          disabled={!canId}
                          onBlur={async (e) => {
                            const v = e.target.value || null;
                            if (v === (f[col] ?? null)) return;
                            const { error } = await saveFlavor(f.id, { [col]: v });
                            if (error) setMsg(`Erro ao salvar sabor: ${error}`);
                            else setFlavors((fs) => fs.map((x) => (x.id === f.id ? { ...x, [col]: v } : x)));
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {flavors.length === 0 && (
                  <tr><td colSpan={3} className="muted">Nenhum sabor cadastrado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <SaveBar blockKey="identificacao" />
        </div>

        {/* 3. Vendas por sabor */}
        <Block blockKey="vendas" title="3. Projeção de Vendas — 12 meses (por sabor)" SaveBar={SaveBar} Seal={Seal}>
          {(() => {
            const can = blockEditable('vendas');
            const v = card.vendas;
            const setSabor = (row: VendaSabor) => upd('vendas', upsertVendaSabor(v, row));
            const tot = vendasTotais(v);
            const aggVol = vendasVolumeMensalAgregado(v);
            const precoMedio = vendasPrecoMedioKg(v);
            return (
              <>
                <div className="capa-grid">
                  <Stat label="Volume total 12M" value={`${tot.volume12m.toLocaleString('pt-BR')} kg`} />
                  <Stat label="Receita bruta 12M" value={brl(tot.receita12m)} />
                  <Stat label="Margem ponderada" value={tot.margemPonderada != null ? `${tot.margemPonderada.toFixed(1)}%` : '—'} />
                </div>
                <VendasChart volumes={aggVol} precoKg={precoMedio} />
                {flavors.length === 0 && (
                  <div className="card card-pad venda-sabor" style={{ boxShadow: 'none' }}>
                    <p className="muted" style={{ marginTop: 0 }}>
                      Sem sabores cadastrados — preencha aqui a projeção agregada dos 12 meses.
                      (Ao cadastrar sabores na Identificação, a projeção passa a ser por SKU.)
                    </p>
                    <div className="capa-grid">
                      <Numb label="Preço de venda (R$/kg)" value={v.preco_kg} disabled={!can}
                        onChange={(x) => upd('vendas', { ...v, preco_kg: x })} />
                    </div>
                    <div className="capa-months">
                      {MESES.map((m, i) => {
                        const vols = v.volume_mensal ?? Array(12).fill(0);
                        return (
                          <label key={m} className="capa-month">
                            <span className="muted">{m}</span>
                            <input type="number" min={0} value={vols[i] || ''} disabled={!can}
                              onChange={(e) => {
                                const next = [...(v.volume_mensal ?? Array(12).fill(0))];
                                next[i] = num(e.target.value) ?? 0;
                                upd('vendas', { ...v, volume_mensal: next });
                              }} />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                {flavors.map((f) => {
                  const s = vendaSaborFor(v, f.id);
                  const vols = s.volume_mensal ?? Array(12).fill(0);
                  const base = (patch: Partial<VendaSabor>) =>
                    setSabor({ ...s, flavorId: f.id, nome: f.name, ...patch });
                  const setVol = (i: number, val: number | undefined) => {
                    const next = [...vols];
                    next[i] = val ?? 0;
                    base({ volume_mensal: next });
                  };
                  const vol12 = vendaVolume12m(s);
                  const marg = vendaMargem(s);
                  const dev = desviosSabor(card.viabilidade as Premissas, s);
                  return (
                    <div className="card card-pad venda-sabor" key={f.id} style={{ boxShadow: 'none' }}>
                      <div className="venda-sabor-head">
                        <strong>{f.name}</strong>
                        <span className="muted">
                          {vol12.toLocaleString('pt-BR')} kg · {brl(vol12 * (s.preco_kg ?? 0))}
                          {marg != null ? ` · MC ${marg.toFixed(1)}%` : ''}
                        </span>
                      </div>
                      <div className="capa-grid">
                        <Numb label="Custo (R$/kg)" value={s.custo_kg} disabled={!can} bad={dev.custoKg} hint={`acima do CPV estimado (${card.viabilidade.cpv_kg})`} onChange={(x) => base({ custo_kg: x })} />
                        <Numb label="Preço (R$/un)" value={s.preco_un} disabled={!can} bad={dev.precoUn} hint={`abaixo do PV estimado (${card.viabilidade.preco_venda_un})`} onChange={(x) => base({ preco_un: x })} />
                        <Numb label="Preço (R$/kg)" value={s.preco_kg} disabled={!can} bad={dev.precoKg} hint={`abaixo do PV estimado (${card.viabilidade.preco_venda_kg})`} onChange={(x) => base({ preco_kg: x })} />
                      </div>
                      {dev.margem && (
                        <div className="verdict bad">Margem deste sabor ({marg?.toFixed(1)}%) abaixo da margem mínima exigida ({card.viabilidade.margem_min}%).</div>
                      )}
                      <div className="capa-months">
                        {MESES.map((m, i) => (
                          <label key={m} className="capa-month">
                            <span className="muted">{m}</span>
                            <input type="number" min={0} value={vols[i] || ''} disabled={!can}
                              onChange={(e) => setVol(i, num(e.target.value))} />
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <Area label="Premissas da projeção" value={v.premissas} disabled={!can} onChange={(x) => upd('vendas', { ...v, premissas: x })} />
              </>
            );
          })()}
        </Block>

        {/* 4. Viabilidade */}
        <Block blockKey="viabilidade" title="4. Viabilidade" SaveBar={SaveBar} Seal={Seal}>
          {(() => {
            const can = blockEditable('viabilidade');
            const v = card.viabilidade;
            const set = (patch: Partial<typeof v>) => upd('viabilidade', { ...v, ...patch });
            const calc = margemPrevista(v); // sugestão calculada a partir de CMP e preço
            const mp = v.mc_prevista ?? calc; // valor usado: o digitado tem prioridade
            const viavel = mp != null && v.margem_min != null ? mp >= v.margem_min : null;
            return (
              <>
                <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                  <b>Premissas obrigatórias</b> da etapa <b>{ETAPA_VIABILIDADE_PADRAO}. {NOME_ETAPA_VIABILIDADE}</b> (Custos e Marketing).
                  Com todas preenchidas e salvas, a etapa é concluída automaticamente no PD1. O que o Comercial preencher na
                  Projeção de Vendas é conferido contra elas (campos fora da premissa ficam em vermelho).
                </p>
                <div className="capa-grid">
                  <Numb label="CPV estimado (R$/kg)" value={v.cpv_kg} disabled={!can} onChange={(x) => set({ cpv_kg: x })} />
                  <Numb label="CPV estimado (R$/un.) — calculado" value={cpvUnitario(v.cpv_kg, card.identificacao.gramatura)} disabled onChange={() => {}} />
                  <Numb label="PV estimado (R$/kg)" value={v.preco_venda_kg} disabled={!can} onChange={(x) => set({ preco_venda_kg: x })} />
                  <Numb label="PV estimado (R$/un.)" value={v.preco_venda_un} disabled={!can} onChange={(x) => set({ preco_venda_un: x })} />
                  <Numb label="PCO — preço sugerido ao consumidor (R$/un.)" value={v.pco_un} disabled={!can} onChange={(x) => set({ pco_un: x })} />
                  <Numb label="Margem mínima exigida (%)" value={v.margem_min} disabled={!can} onChange={(x) => set({ margem_min: x })} />
                  <Numb label="Margem de contribuição prevista (MC %)" value={v.mc_prevista} disabled={!can} onChange={(x) => set({ mc_prevista: x })} />
                </div>
                <p className="muted" style={{ fontSize: 12 }}>
                  {card.identificacao.gramatura
                    ? `CPV/un. = CPV/kg × gramatura (${card.identificacao.gramatura}), calculado automaticamente.`
                    : 'Preencha a gramatura na Identificação para o painel calcular o CPV por unidade.'}
                </p>
                {premissasFaltando(viabilidadeComDerivados(v, card.identificacao.gramatura) as Premissas).length > 0 && (
                  <p className="muted" style={{ fontSize: 12 }}>
                    Faltam para concluir a etapa: <b>{premissasFaltando(viabilidadeComDerivados(v, card.identificacao.gramatura) as Premissas).map((k) => PREMISSA_ROTULO[k]).join(', ')}</b>.
                  </p>
                )}
                {calc != null && (
                  <p className="muted" style={{ fontSize: 12, marginTop: -2 }}>
                    Sugestão calculada por CMP e preço: <b>{calc.toFixed(1)}%</b>. A MC acima é editável — se
                    deixar em branco, usamos a sugestão.
                  </p>
                )}
                {viavel != null && (
                  <div className={`verdict ${viavel ? 'ok' : 'bad'}`}>
                    {viavel
                      ? `Viável — MC prevista (${mp!.toFixed(1)}%) ≥ mínima (${v.margem_min}%).`
                      : `Atenção — MC prevista (${mp!.toFixed(1)}%) abaixo da mínima (${v.margem_min}%).`}
                  </div>
                )}
              </>
            );
          })()}
        </Block>

        {/* 5. Preço */}
        <Block blockKey="preco" title="5. Preço e Posicionamento" SaveBar={SaveBar} Seal={Seal}>
          {(() => {
            const can = blockEditable('preco');
            const p = card.preco;
            const set = (patch: Partial<typeof p>) => upd('preco', { ...p, ...patch });
            return (
              <>
              <div className="capa-grid">
                <Numb label="Preço de venda (indústria)" value={p.preco_industria} disabled={!can} onChange={(x) => set({ preco_industria: x })} />
                <Numb label="PMZ — gôndola regular" value={p.pmz} disabled={!can} onChange={(x) => set({ pmz: x })} />
                <Text label="Preço promocionado" value={p.preco_promo} disabled={!can} onChange={(x) => set({ preco_promo: x })} />
              </div>
              <AncoraCapa p={p} can={can} set={set} gramaturaNossa={card.identificacao.gramatura} pcoUn={card.viabilidade.pco_un} />
              </>
            );
          })()}
        </Block>

        {/* 6. Concorrência */}
        <Block blockKey="concorrencia" title="6. Pesquisa de Concorrência" SaveBar={SaveBar} Seal={Seal}>
          {(() => {
            const can = blockEditable('concorrencia');
            const comps = card.concorrencia.competidores ?? [];
            const setComps = (next: Competidor[]) => upd('concorrencia', { competidores: next });
            return (
              <>
                <div className="proj-grid">
                  {comps.map((c, ci) => {
                    const coletas = c.coletas ?? [];
                    const precos = coletas.map((x) => x.preco ?? 0).filter((x) => x > 0);
                    const media = precos.length ? precos.reduce((a, b) => a + b, 0) / precos.length : 0;
                    const upComp = (patch: Partial<Competidor>) =>
                      setComps(comps.map((x, i) => (i === ci ? { ...x, ...patch } : x)));
                    return (
                      <div className="card card-pad" key={ci} style={{ boxShadow: 'none' }}>
                        <Text label="Concorrente" value={c.nome} disabled={!can} onChange={(v) => upComp({ nome: v })} />
                        <Text label="Produto" value={c.produto} disabled={!can} onChange={(v) => upComp({ produto: v })} />
                        <div className="capa-coletas">
                          {coletas.map((col, xi) => (
                            <div className="capa-coleta" key={xi}>
                              <input placeholder="Local" defaultValue={col.local ?? ''} disabled={!can}
                                onBlur={(e) => upComp({ coletas: coletas.map((y, i) => (i === xi ? { ...y, local: e.target.value } : y)) })} />
                              <input type="date" defaultValue={col.data ?? ''} disabled={!can}
                                onBlur={(e) => upComp({ coletas: coletas.map((y, i) => (i === xi ? { ...y, data: e.target.value } : y)) })} />
                              <input type="number" placeholder="R$" defaultValue={col.preco ?? ''} disabled={!can}
                                onBlur={(e) => upComp({ coletas: coletas.map((y, i) => (i === xi ? { ...y, preco: num(e.target.value) } : y)) })} />
                            </div>
                          ))}
                          {can && (
                            <button className="btn-ghost btn-sm" onClick={() => upComp({ coletas: [...coletas, {}] })}>+ coleta</button>
                          )}
                        </div>
                        <div className="muted" style={{ marginTop: 6 }}>Preço médio: <b>{media ? brl(media) : '—'}</b></div>
                        {can && (
                          <button className="btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={() => setComps(comps.filter((_, i) => i !== ci))}>Remover concorrente</button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {can && (
                  <button className="btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setComps([...comps, { coletas: [] }])}>+ Concorrente</button>
                )}
              </>
            );
          })()}
        </Block>
        </>
        )}
      </div>

      {histFor && (
        <div className="modal-backdrop" onClick={() => setHistFor(null)}>
          <div className="modal card" onClick={(e) => e.stopPropagation()}>
            <div className="section-title">
              <h2>Histórico — {BLOCK_TITLES[histFor]}</h2>
              <button className="btn-ghost btn-sm" onClick={() => setHistFor(null)}>Fechar</button>
            </div>
            {histLoading ? (
              <p className="muted">Carregando…</p>
            ) : histRows.length === 0 ? (
              <p className="muted">Nenhuma versão registrada ainda.</p>
            ) : (
              <div className="hist-list">
                {histRows.map((v, i) => (
                  <div className="hist-item" key={v.id}>
                    <div className="hist-head">
                      <b>{i === 0 ? 'Versão atual' : `Versão ${histRows.length - i}`}</b>
                      <span className="muted">
                        {v.byName ?? '—'} · {new Date(v.at).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <pre className="hist-json">{JSON.stringify(v.value, null, 1)}</pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <FloatingBack to={`/produto/${id}`} label="Voltar às etapas" />
    </>
  );
}

// ---- Componentes auxiliares ------------------------------------------------
function Block({
  blockKey, title, SaveBar, Seal, children,
}: {
  blockKey: BlockKey;
  title: string;
  SaveBar: (p: { blockKey: BlockKey }) => ReactElement;
  Seal: (p: { blockKey: BlockKey }) => ReactElement;
  children: ReactNode;
}) {
  return (
    <div className="card card-pad capa-block" id={`bloco-${blockKey}`}>
      <div className="section-title">
        <h2>{title}</h2>
        <Seal blockKey={blockKey} />
      </div>
      {children}
      <SaveBar blockKey={blockKey} />
    </div>
  );
}

function Text({ label, value, onChange, disabled }: { label: string; value?: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Area({ label, value, onChange, disabled }: { label: string; value?: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea rows={2} value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Numb({ label, value, onChange, disabled, bad, hint }: { label: string; value?: number; onChange: (v: number | undefined) => void; disabled?: boolean; bad?: boolean; hint?: string }) {
  return (
    <label className={`field${bad ? ' invalid' : ''}`} title={bad ? hint : undefined}>
      <span>{label}{bad && hint ? <em className="hint-bad"> — {hint}</em> : null}</span>
      <input type="number" value={value ?? ''} disabled={disabled}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="capa-stat">
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function VendasChart({ volumes, precoKg }: { volumes: number[]; precoKg: number }) {
  const w = 640, h = 140, pad = 6;
  const maxVol = Math.max(1, ...volumes);
  const bw = (w - pad * 2) / 12;
  const receitas = volumes.map((v) => (v || 0) * precoKg);
  const maxRec = Math.max(1, ...receitas);
  const pts = receitas
    .map((r, i) => `${pad + bw * i + bw / 2},${h - pad - (r / maxRec) * (h - pad * 2)}`)
    .join(' ');
  return (
    <svg className="vendas-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label="Projeção de vendas">
      {volumes.map((v, i) => {
        const bh = ((v || 0) / maxVol) * (h - pad * 2);
        return <rect key={i} x={pad + bw * i + 2} y={h - pad - bh} width={bw - 4} height={bh} rx={2} fill="var(--accent)" opacity={0.55} />;
      })}
      {precoKg > 0 && <polyline points={pts} fill="none" stroke="var(--brand-green)" strokeWidth={2} />}
    </svg>
  );
}

const PREMISSA_ROTULO: Record<keyof Premissas, string> = {
  cpv_kg: 'CPV estimado (kg)',
  cpv_un: 'CPV estimado (un.)',
  preco_venda_kg: 'PV estimado (kg)',
  preco_venda_un: 'PV estimado (un.)',
  pco_un: 'PCO sugerido',
  margem_min: 'margem mínima',
};

/** Produto de ancoragem na Capa: o Marketing define o item da concorrência e a elasticidade; o Comercial vê o preço-alvo. */
function AncoraCapa({
  p, can, set, gramaturaNossa, pcoUn,
}: {
  p: import('../lib/capa').Preco;
  can: boolean;
  set: (patch: Partial<import('../lib/capa').Preco>) => void;
  gramaturaNossa?: string;
  pcoUn?: number;
}) {
  const difere = gramaturasDiferem(gramaturaNossa, p.ancora_gramatura);
  const anc = {
    nome: p.ancora_nome ?? null,
    gramatura: p.ancora_gramatura ?? null,
    elasticidade: p.ancora_elasticidade ?? null,
    base: (difere ? p.ancora_base ?? 'un' : 'un') as 'un' | 'kg',
  };
  const cmp = compararPco(pcoUn, gramaturaNossa, anc, p.ancora_preco);
  const el = p.ancora_elasticidade;
  const alvo = el != null && p.ancora_preco ? (p.ancora_preco * el) / 100 : null;
  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>Produto de ancoragem (orientação de preço para o Comercial)</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Elasticidade = % do preço do âncora que precisamos praticar NO MÍNIMO (ex.: 90 ou 108); abaixo disso perdemos
        rentabilidade. No painel de lançamentos, o preço do âncora é preenchido todo mês e a elasticidade vira a meta.
      </p>
      <div className="capa-grid">
        <Text label="Item da concorrência (âncora)" value={p.ancora_nome} disabled={!can} onChange={(x) => set({ ancora_nome: x })} />
        <Numb label="Preço atual do âncora ao consumidor (R$/un.)" value={p.ancora_preco} disabled={!can} onChange={(x) => set({ ancora_preco: x })} />
        <Numb label="Elasticidade (% do preço do âncora)" value={p.ancora_elasticidade} disabled={!can} onChange={(x) => set({ ancora_elasticidade: x })} />
        <Text label="Gramatura do âncora (vazio = igual à nossa)" value={p.ancora_gramatura} disabled={!can} onChange={(x) => set({ ancora_gramatura: x })} />
      </div>
      {difere && (
        <div className="verdict bad">
          A gramatura do âncora ({p.ancora_gramatura}) difere da nossa ({gramaturaNossa}). A elasticidade deve valer por:
          <label style={{ marginLeft: 12 }}><input type="radio" disabled={!can} checked={(p.ancora_base ?? 'un') === 'un'} onChange={() => set({ ancora_base: 'un' })} /> unidade</label>
          <label style={{ marginLeft: 12 }}><input type="radio" disabled={!can} checked={p.ancora_base === 'kg'} onChange={() => set({ ancora_base: 'kg' })} /> kg</label>
        </div>
      )}
      {alvo != null && (
        <p style={{ fontSize: 13 }}>
          Preço-alvo ao consumidor: <b>{alvo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</b>/un.
          {' (no mínimo)'}
        </p>
      )}
      {cmp && cmp.ok != null && (
        <div className={`verdict ${cmp.ok ? 'ok' : 'bad'}`}>
          {cmp.ok ? 'Dentro da meta' : 'Fora da meta (abaixo do mínimo)'} — o PCO sugerido ({cmp.nossoUn.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}) está em{' '}
          {cmp.pct.toFixed(1)}% do âncora; mínimo {el}%.
        </div>
      )}
    </div>
  );
}
